/**
 * SoleadLab 2.0 - Confirm Dialog
 *
 * Renders the global confirm dialog driven by uiStore.confirmDialog.
 * Mount once in EditorPage — it auto-shows/hides based on store state.
 */

import { useEffect, useRef } from 'react';
import { useUIStore } from '../../stores';
import Modal from './Modal';
import Button from './Button';

export default function ConfirmDialog() {
  const confirmDialog = useUIStore((s) => s.confirmDialog);
  const hideConfirm = useUIStore((s) => s.hideConfirm);

  // M-05A: destructive confirmations default focus to Cancel, so a stray
  // Enter/Space cannot trigger the danger action. (The dialog has no form,
  // so the only Enter risk is an activated button — keeping Cancel focused
  // makes Enter resolve to Cancel.) Non-destructive dialogs are unchanged.
  const cancelButtonRef = useRef<HTMLButtonElement>(null);
  const isDanger = confirmDialog?.variant === 'danger';

  useEffect(() => {
    if (confirmDialog && isDanger) {
      cancelButtonRef.current?.focus();
    }
  }, [confirmDialog, isDanger]);

  if (!confirmDialog) return null;

  const {
    title,
    message,
    confirmLabel = 'Confirm',
    cancelLabel = 'Cancel',
    variant = 'default',
    onConfirm,
    onCancel,
  } = confirmDialog;

  const handleConfirm = () => {
    hideConfirm();
    onConfirm();
  };

  const handleCancel = () => {
    hideConfirm();
    onCancel?.();
  };

  return (
    <Modal
      isOpen={true}
      onClose={handleCancel}
      title={title}
      size="sm"
      footer={
        <>
          <Button ref={cancelButtonRef} variant="secondary" size="sm" onClick={handleCancel}>
            {cancelLabel}
          </Button>
          <Button
            variant={variant === 'danger' ? 'danger' : 'primary'}
            size="sm"
            onClick={handleConfirm}
          >
            {confirmLabel}
          </Button>
        </>
      }
    >
      <p style={{ margin: 0, fontSize: 14, lineHeight: 1.5, color: 'var(--sl-text-secondary)' }}>
        {message}
      </p>
    </Modal>
  );
}
